import { useMutation, useQueryClient } from '@tanstack/react-query'
import { generateSlot, rerunSlot, selectSlotGeneration } from '@/lib/api/amazon-listing'
import type { ImageSlot } from '@/lib/api/amazon-listing.types'

export function useSlotGenerate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (slotId: string) => generateSlot(slotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['image-slots'] })
    },
  })
}

export function useSlotRerun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: {
      slotId: string
      prompt?: string
      overlayText?: string
      infoOverlayText?: string
      fontFamily?: string
      editInstructions?: string
      resolution?: string
      aspectRatio?: string
      referenceImageUrls?: string[]
      generationCount?: number
    }) => rerunSlot(params.slotId, {
      modified_prompt: params.prompt,
      overlay_text: params.overlayText,
      info_overlay_text: params.infoOverlayText,
      font_family: params.fontFamily,
      edit_instructions: params.editInstructions,
      resolution: params.resolution,
      aspect_ratio: params.aspectRatio,
      reference_image_urls: params.referenceImageUrls,
      generation_count: params.generationCount,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['image-slots'] })
    },
  })
}

export function useSlotSelect() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { slotId: string; generationId: string }) =>
      selectSlotGeneration(params.slotId, params.generationId),
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: ['image-slots'] })
      const allQueries = queryClient.getQueriesData<ImageSlot[]>({ queryKey: ['image-slots'] })
      for (const [key, slots] of allQueries) {
        if (!slots) continue
        queryClient.setQueryData(key, slots.map(s =>
          s.id === params.slotId ? { ...s, selected_generation_id: params.generationId } : s,
        ))
      }
      return { allQueries }
    },
    onError: (_err, _params, context) => {
      if (context?.allQueries) {
        for (const [key, slots] of context.allQueries) {
          queryClient.setQueryData(key, slots)
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['image-slots'] })
    },
  })
}
